/**
 * TelegramSection — Paritaets-Runde 3 / Stream 2
 *
 * Shared-Bot-Flow wie im v1-Monolithen (app/views/account/show.html.erb:117-132):
 * Der User oeffnet den globalen HeliumTracker-Bot ueber den Deep-Link und sendet
 * seinen Connect-Token; der Bot setzt die chat_id. Eine manuelle Chat-ID-Eingabe
 * gibt es bewusst NICHT mehr.
 *
 * Zusaetzlich: Token teilmaskiert mit Reveal/Copy, Regenerate, Disconnect,
 * Zustell-Feinsteuerung (enabled / notify_income / notify_activity / mute)
 * sowie Bring-your-own-Bot als optionaler Advanced-Bereich.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useTelegram } from './useTelegram'
import { apiErrorMessage } from '../../lib/errors/apiErrorMessage'
import { activeBcp47, activeDateFnsLocale, isEnLocale } from '../../lib/locale'

export function TelegramSection() {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const {
    settings,
    isLoading,
    isError,
    refetch,
    updateSettings,
    isUpdating,
    disconnect,
    isDisconnecting,
    regenerateToken,
    isRegenerating,
    sendTestMessage,
    isSendingTest,
  } = useTelegram()

  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [botToken, setBotToken] = useState('')
  const [toast, setToast] = useState<{ ok: boolean; message: string } | null>(null)

  const isConnected = Boolean(settings?.connected)
  const connectToken = settings?.connect_token ?? null
  const maskedToken = settings?.connect_token_masked ?? null
  const deepLink = settings?.deep_link ?? null
  // BE-Contract liefert je nach Stand `bot_token_set` oder das Boolean `bot_token`.
  const hasBotToken = Boolean(settings?.bot_token_set ?? settings?.bot_token)
  const trimmedBotToken = botToken.trim()
  // heliumtracker-web#18: Ohne Deep-Link ist der globale Bot nicht konfiguriert
  // (solange noch keine Verbindung besteht — danach ist deep_link regulaer null,
  // siehe Kommentar bei der Verbindungs-Anleitung weiter unten). In diesem Fall
  // sind Test/Disconnect/Regenerate wirkungslos und muessen deaktiviert bleiben.
  const botConfigured = isConnected || Boolean(deepLink)

  // R3-Review F9: Timer in Refs + Cleanup beim Unmount — sonst feuert das
  // setTimeout in eine bereits abgeraeumte Komponente (React-State-Warning).
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current)
      if (copiedTimer.current) clearTimeout(copiedTimer.current)
    }
  }, [])

  function showToast(ok: boolean, message: string) {
    setToast({ ok, message })
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 4000)
  }

  async function handleCopy() {
    if (!connectToken) return
    try {
      await navigator.clipboard?.writeText(connectToken)
      setCopied(true)
      if (copiedTimer.current) clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      showToast(false, t('telegram.copyError'))
    }
  }

  async function handleTest() {
    try {
      const result = await sendTestMessage()
      showToast(result.ok, result.message || t('telegram.testSuccess'))
      refetch()
    } catch (e) {
      showToast(false, apiErrorMessage(e, t('telegram.testError')))
    }
  }

  async function handleRegenerate() {
    if (!window.confirm(t('telegram.regenerateConfirm'))) return
    try {
      await regenerateToken()
      setRevealed(false)
      showToast(true, t('telegram.regenerateSuccess'))
    } catch (e) {
      showToast(false, apiErrorMessage(e, t('telegram.regenerateError')))
    }
  }

  async function handleDisconnect() {
    if (!window.confirm(t('telegram.disconnectConfirm'))) return
    try {
      await disconnect()
      showToast(true, t('telegram.disconnectSuccess'))
    } catch (e) {
      showToast(false, apiErrorMessage(e, t('telegram.disconnectError')))
    }
  }

  async function handlePatch(payload: Parameters<typeof updateSettings>[0]) {
    try {
      await updateSettings(payload)
      showToast(true, t('telegram.saveSuccess'))
      return true
    } catch (e) {
      showToast(false, apiErrorMessage(e, t('telegram.saveError')))
      return false
    }
  }

  /**
   * R3-Review F1 (Blocker): Frueher ging `bot_token: botToken || null` in den
   * PATCH — ein leeres Feld hat damit den hinterlegten BYO-Token GELOESCHT
   * (das Feld ist nach jedem Reload leer, weil das BE den Token nie zurueckgibt).
   * Jetzt wird bot_token nur bei nicht-leerer Eingabe gesendet; das Entfernen
   * ist eine eigene, bestaetigte Aktion (handleRemoveBotToken).
   */
  async function handleSaveBotToken() {
    if (!trimmedBotToken) return
    const ok = await handlePatch({ bot_token: trimmedBotToken })
    // F9: Klartext-Token nach erfolgreichem Save nicht im State halten.
    if (ok) setBotToken('')
  }

  async function handleRemoveBotToken() {
    if (!window.confirm(t('telegram.botTokenRemoveConfirm'))) return
    try {
      await updateSettings({ bot_token: null })
      setBotToken('')
      showToast(true, t('telegram.botTokenRemoved'))
    } catch (e) {
      showToast(false, apiErrorMessage(e, t('telegram.saveError')))
    }
  }

  if (isLoading) {
    return (
      <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="telegram-section">
        <div className="skeleton" style={{ height: 80 }} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="telegram-section">
        <p className="muted">{t('telegram.loadError')}</p>
        <button type="button" className="btn btn-sm" onClick={() => refetch()}>
          {tc('actions.retry')}
        </button>
      </div>
    )
  }

  return (
    <div
      className="card card-pad"
      style={{ marginBottom: 18 }}
      data-testid="telegram-section"
      id="telegram"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>{t('telegram.title')}</h3>
        {isConnected ? (
          <span className="pill pill-primary" data-testid="telegram-connected-badge">
            {t('telegram.connected')}
          </span>
        ) : (
          <span
            className="pill"
            data-testid="telegram-disconnected-badge"
            style={{ background: 'var(--c-danger, #ef4444)', color: '#fff' }}
          >
            {t('telegram.disconnected')}
          </span>
        )}
      </div>

      {/* Statusbanner — Wortlaut wie v1 (account/show.html.erb:124-129) */}
      <div
        data-testid="telegram-status-banner"
        style={{
          marginBottom: 14,
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: 13,
          border: '1px solid var(--border)',
          background: isConnected ? 'rgba(34,197,94,0.12)' : 'rgba(234,179,8,0.12)',
        }}
      >
        {isConnected ? t('telegram.connectedHint') : t('telegram.notConnectedHint')}
      </div>

      {/* Story 3.4: Chat-Info im Verbunden-Zustand (Design-Referenz settings.html) */}
      {isConnected && (
        <div
          data-testid="telegram-chat-info"
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface-3)',
              fontSize: 16,
            }}
          >
            ✈️
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {t('telegram.chatIdLabel', { chatId: settings?.chat_id ?? '—' })}
            </div>
            {settings?.verified_at && (
              <div className="muted" style={{ fontSize: 11 }}>
                {/* F9: Datum folgt der aktiven i18n-Sprache wie im Rest der App
                    (vorher Browser-Default via toLocaleDateString()). */}
                {t('telegram.connectedSince', {
                  date: format(
                    new Date(settings.verified_at),
                    isEnLocale() ? 'MMM d, yyyy' : 'd. MMM yyyy',
                    { locale: activeDateFnsLocale() },
                  ),
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/*
        Deep-Link — nur relevant, solange NICHT verbunden. `deep_link` ist nach
        dem Connect regulaer null (der Bot ist bereits gesetzt); ihn dann als
        "Bot nicht konfiguriert" anzuzeigen widerspricht dem Verbunden-Badge
        (heliumtracker-web#18). Im verbundenen Zustand entfaellt der Block.
      */}
      {!isConnected && (
        <div style={{ marginBottom: 14 }}>
          {deepLink ? (
            <a
              className="btn btn-primary btn-sm"
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telegram-deeplink"
            >
              {t('telegram.openBot')}
            </a>
          ) : (
            <p className="muted" style={{ fontSize: 12 }} data-testid="telegram-deeplink-missing">
              {t('telegram.botUnavailable')}
            </p>
          )}
        </div>
      )}

      {/*
        Aktionen — Design-Referenz settings.html zeigt im Verbunden-Zustand
        NUR "Send test" + "Disconnect" (kompakte Karte, P1-Fund). Connect-Token,
        Regenerate, Zustell-Feinsteuerung und Bring-your-own-Bot sind reale,
        aber sekundaere Funktionen und wandern deshalb ins collapsed "Advanced"
        (siehe unten) statt die Karte permanent aufzublaehen.
        heliumtracker-web#18: Ohne Verbindung sind Test+Disconnect wirkungslos
        (kein Chat zum Testen, nichts zu trennen) — im nicht verbundenen
        Zustand zeigt die Karte stattdessen nur die Verbindungs-Anleitung oben.
      */}
      {isConnected && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleTest}
            disabled={isSendingTest}
            data-testid="telegram-test-btn"
          >
            {isSendingTest ? t('telegram.sending') : t('telegram.sendTest')}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            data-testid="telegram-disconnect-btn"
          >
            {isDisconnecting ? t('telegram.saving') : t('telegram.disconnect')}
          </button>
        </div>
      )}

      {/* Advanced: Connect-Token, Regenerate, Zustell-Feinsteuerung, Bring-your-own-Bot */}
      <details
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
        data-testid="telegram-advanced"
      >
        <summary style={{ cursor: 'pointer', fontSize: 13 }}>{t('telegram.advanced')}</summary>
        <div style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 14 }}>
            <label className="label">{t('telegram.connectToken')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code
                data-testid="telegram-connect-token"
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  background: 'var(--bg-muted, #f3f4f6)',
                }}
              >
                {connectToken ? (revealed ? connectToken : (maskedToken ?? '••••••')) : '—'}
              </code>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setRevealed((v) => !v)}
                disabled={!connectToken || !botConfigured}
                data-testid="telegram-reveal-btn"
              >
                {revealed ? t('telegram.hide') : t('telegram.reveal')}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleCopy}
                disabled={!connectToken || !botConfigured}
                data-testid="telegram-copy-btn"
              >
                {copied ? t('telegram.copied') : t('telegram.copy')}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleRegenerate}
                disabled={isRegenerating || !botConfigured}
                title={!botConfigured ? t('telegram.botUnavailable') : undefined}
                data-testid="telegram-regenerate-btn"
              >
                {isRegenerating ? t('telegram.saving') : t('telegram.regenerate')}
              </button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {t('telegram.howToConnect')}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={settings?.enabled ?? true}
                disabled={isUpdating}
                onChange={(e) => handlePatch({ enabled: e.target.checked })}
                data-testid="telegram-enabled-toggle"
              />
              {t('telegram.enabled')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={settings?.notify_income ?? false}
                disabled={isUpdating}
                onChange={(e) => handlePatch({ notify_income: e.target.checked })}
                data-testid="telegram-notify-income-toggle"
              />
              {t('telegram.notifyIncome')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={settings?.notify_activity ?? false}
                disabled={isUpdating}
                onChange={(e) => handlePatch({ notify_activity: e.target.checked })}
                data-testid="telegram-notify-activity-toggle"
              />
              {t('telegram.notifyActivity')}
            </label>
            {settings?.muted_until && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span data-testid="telegram-muted-until">
                  {t('telegram.mutedUntil', {
                    date: new Date(settings.muted_until).toLocaleString(activeBcp47()),
                  })}
                </span>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => handlePatch({ muted_until: null })}
                  disabled={isUpdating}
                  data-testid="telegram-unmute-btn"
                >
                  {t('telegram.unmute')}
                </button>
              </div>
            )}
          </div>

          <p className="muted" style={{ fontSize: 12 }}>
            {t('telegram.advancedHint')}
          </p>
          <label className="label" htmlFor="telegram-bot-token">
            {t('telegram.botToken')}
            {hasBotToken && (
              <span
                className="muted"
                style={{ marginLeft: 6, fontSize: 11 }}
                data-testid="telegram-bot-token-set"
              >
                {t('telegram.botTokenSet')}
              </span>
            )}
          </label>
          <input
            id="telegram-bot-token"
            type="password"
            className="input"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="123456:ABC-DEF..."
            style={{ width: '100%', maxWidth: 400 }}
            data-testid="telegram-bot-token-input"
          />
          <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {t('telegram.botTokenKeepHint')}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSaveBotToken}
              disabled={isUpdating || !trimmedBotToken}
              data-testid="telegram-save-btn"
            >
              {isUpdating ? t('telegram.saving') : tc('actions.save')}
            </button>
            {hasBotToken && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleRemoveBotToken}
                disabled={isUpdating}
                data-testid="telegram-bot-token-remove-btn"
              >
                {t('telegram.botTokenRemove')}
              </button>
            )}
          </div>
        </div>
      </details>

      {toast && (
        <div
          data-testid="telegram-toast"
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: toast.ok ? 'var(--c-success, #22c55e)' : 'var(--c-danger, #ef4444)',
            color: '#fff',
            fontSize: 13,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  )
}
